// Shared chrome for the Organization pages (Regions, Dealers, Workshops,
// People):
// the toolbar, the table, the status chip and the two dialogs. Containers are
// white with a hairline border, and status is a neutral chip with a coloured
// dot — same treatment as the rest of the console.

import { Loader2, Plus, Search, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { GhostButton, PrimaryButton, SecondaryButton, Select, cardClass, inputClass } from "./primitives";

export function PageIntro({ title, subtitle, actionLabel, onAction }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-6 pt-2">
      <div className="min-w-0">
        <h1 className="text-[26px] font-semibold leading-[34px] tracking-[-0.02em] text-[#101828]">{title}</h1>
        {subtitle && <p className="mt-1 max-w-[620px] text-[14px] leading-[20px] text-[#667085]">{subtitle}</p>}
      </div>
      {actionLabel && (
        <PrimaryButton onClick={onAction}>
          <Plus size={16} />
          {actionLabel}
        </PrimaryButton>
      )}
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder, ...rest }) {
  return (
    <div className="relative max-w-[340px] flex-[1_1_260px]">
      <Search size={16} className="pointer-events-none absolute left-3 top-3 text-[#9CA3AF]" />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputClass} pl-9`}
        {...rest}
      />
    </div>
  );
}

export function Toolbar({ children, count }) {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3">
      {children}
      {count != null && <span className="ml-auto text-[13px] text-[#6B7280]">{count}</span>}
    </div>
  );
}

export function FilterSelect({ value, onChange, options, ...rest }) {
  return (
    <Select className="min-w-[160px]" value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

const DOT = {
  active: "bg-[#16A34A]",
  paused: "bg-[#B45309]",
  invited: "bg-[#B45309]",
  disabled: "bg-[#9CA3AF]",
  no_login: "bg-[#9CA3AF]",
};
const WORD = {
  active: "Active",
  paused: "Paused",
  invited: "Invited",
  disabled: "Disabled",
  no_login: "No login",
};

export function StatusChip({ status }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#F9FAFB] px-2.5 py-1 text-[12px] font-medium text-[#4B5563]">
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[status] || "bg-[#9CA3AF]"}`} />
      {WORD[status] || status}
    </span>
  );
}

export function Table({ columns, children }) {
  return (
    <div className={`mt-[18px] overflow-hidden ${cardClass}`}>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c}
                className="bg-[#F9FAFB] px-[18px] py-[11px] text-left text-[12px] font-medium text-[#667085] shadow-[inset_0_-1px_0_#EAECF0]"
              >
                {c}
              </th>
            ))}
            <th className="w-24 bg-[#F9FAFB] shadow-[inset_0_-1px_0_#EAECF0]" />
          </tr>
        </thead>
        <tbody className="[&>tr:last-child>td]:shadow-none [&>tr]:transition-colors [&>tr]:duration-150 [&>tr:hover]:bg-[#FAFBFC]">{children}</tbody>
      </table>
    </div>
  );
}

export function Cell({ children, className = "" }) {
  return (
    <td className={`px-[18px] py-[14px] align-middle text-[14px] text-[#475467] shadow-[inset_0_-1px_0_#F0F1F3] ${className}`}>
      {children}
    </td>
  );
}

export function DeleteCell({ onDelete, label }) {
  return (
    <Cell className="text-right">
      <button
        type="button"
        onClick={onDelete}
        aria-label={label}
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-[#9CA3AF] transition-colors duration-150 hover:bg-[#FEF2F2] hover:text-[#DC2626]"
      >
        <Trash2 size={16} />
      </button>
    </Cell>
  );
}

export function EmptyState({ title, body, actionLabel, onAction }) {
  return (
    <div className={`mt-[18px] px-8 py-12 text-center ${cardClass}`}>
      <div className="text-[16px] font-semibold text-[#111827]">{title}</div>
      <p className="mx-auto mt-2 max-w-[420px] text-[13px] leading-[20px] text-[#6B7280]">{body}</p>
      {actionLabel && (
        <div className="mt-[18px] flex justify-center">
          <PrimaryButton onClick={onAction}>
            <Plus size={16} />
            {actionLabel}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, description, onSubmit, submitLabel, pending, children }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {children}
          <div className="flex items-center gap-3">
            <PrimaryButton type="submit" disabled={pending}>
              {pending && <Loader2 className="animate-spin" size={14} />}
              {submitLabel}
            </PrimaryButton>
            <GhostButton type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </GhostButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Confirmation before a delete. `blocked` turns it into an explanation of
 *  why it cannot happen yet, with no destructive button to press. */
export function ConfirmDelete({ open, onOpenChange, name, body, blocked, onConfirm, pending }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{blocked ? `Can't delete ${name}` : `Delete ${name}?`}</DialogTitle>
          <DialogDescription>{blocked || body}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          {!blocked && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={pending}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#DC2626] px-3.5 text-[14px] font-medium text-white transition-colors duration-150 hover:bg-[#B91C1C] disabled:opacity-60"
            >
              {pending && <Loader2 className="animate-spin" size={14} />}
              Delete
            </button>
          )}
          <GhostButton onClick={() => onOpenChange(false)}>{blocked ? "Close" : "Cancel"}</GhostButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A query that failed says so. Without this a 502 renders as a spinner that
 *  never resolves, and because failures are not cached every click retries
 *  and spins again — which reads as "it loads every time". */
export function ErrorState({ error, onRetry }) {
  const detail = error?.response?.data?.detail || error?.message || "Something went wrong.";
  return (
    <div className={`mt-[18px] px-8 py-12 text-center ${cardClass}`}>
      <div className="text-[16px] font-semibold text-[#111827]">Couldn&apos;t load this</div>
      <p className="mx-auto mt-2 max-w-[460px] text-[13px] leading-[20px] text-[#6B7280]">{detail}</p>
      {onRetry && (
        <div className="mt-[18px] flex justify-center">
          <SecondaryButton onClick={onRetry}>Try again</SecondaryButton>
        </div>
      )}
    </div>
  );
}

export function Loading() {
  return (
    <div className="mt-7 flex items-center gap-2 text-[13px] text-[#6B7280]">
      <Loader2 className="animate-spin" size={15} /> Loading
    </div>
  );
}
